// The adaptation controls. Used by the Studio and the class-profile editor.
import { CEFR } from "../../shared/catalog";
import { EMPHASES, EMPHASIS_LABELS, MODES, MODE_LABELS, TECH, TECH_LABELS, type Emphasis } from "../../shared/constants";
import type { AdaptParams } from "../lib/types";

type P = Omit<AdaptParams, "includeKit"> & { includeKit?: boolean };

export function Dials<T extends P>({ value, onChange, disabled }: { value: T; onChange: (v: T) => void; disabled?: boolean }) {
  const set = <K extends keyof T>(k: K, v: T[K]) => onChange({ ...value, [k]: v });
  const toggleEmphasis = (e: Emphasis) => {
    const has = value.emphasis.includes(e);
    if (!has && value.emphasis.length >= 4) return;
    set("emphasis", (has ? value.emphasis.filter((x) => x !== e) : [...value.emphasis, e]) as T["emphasis"]);
  };
  const solo = value.mode === "one-to-one";
  return (
    <fieldset className="dials" disabled={disabled}>
      <div className="dial">
        <span className="dial-label" id="lvl">Level</span>
        <div className="seg-control" role="radiogroup" aria-labelledby="lvl">
          {CEFR.map((l) => (
            <button type="button" key={l} role="radio" aria-checked={value.level === l} className={value.level === l ? "on" : ""} onClick={() => set("level", l as T["level"])}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="dial">
        <label className="dial-label" htmlFor="dur">
          Lesson length <output>{value.duration} min</output>
        </label>
        <input id="dur" type="range" min={10} max={120} step={5} value={value.duration} onChange={(e) => set("duration", Number(e.target.value) as T["duration"])} />
        <div className="chips">
          {[30, 40, 60, 80].map((d) => (
            <button type="button" key={d} className={`chip${value.duration === d ? " on" : ""}`} onClick={() => set("duration", d as T["duration"])}>
              {d === 80 ? "Double (80)" : `${d}`}
            </button>
          ))}
        </div>
      </div>

      <div className="dial">
        <span className="dial-label" id="mode">Setting</span>
        <div className="seg-control seg-wrap" role="radiogroup" aria-labelledby="mode">
          {MODES.map((m) => (
            <button type="button" key={m} role="radio" aria-checked={value.mode === m} className={value.mode === m ? "on" : ""} onClick={() => set("mode", m as T["mode"])}>
              {MODE_LABELS[m]}
            </button>
          ))}
        </div>
      </div>

      <div className="dial">
        <label className="dial-label" htmlFor="size">
          Class size <output>{solo ? "1 student" : `${value.classSize} students`}</output>
        </label>
        <input id="size" type="range" min={2} max={80} value={solo ? 2 : value.classSize} disabled={solo} onChange={(e) => set("classSize", Number(e.target.value) as T["classSize"])} />
      </div>

      <div className="dial">
        <span className="dial-label" id="tech">What you have in the room</span>
        <div className="seg-control seg-wrap" role="radiogroup" aria-labelledby="tech">
          {TECH.map((t) => (
            <button type="button" key={t} role="radio" aria-checked={value.tech === t} className={value.tech === t ? "on" : ""} onClick={() => set("tech", t as T["tech"])}>
              {TECH_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      <div className="dial-row">
        <div className="dial">
          <label className="dial-label" htmlFor="ctx">Where you teach</label>
          <input id="ctx" maxLength={160} placeholder="e.g. rural secondary school in Sabah" value={value.context} onChange={(e) => set("context", e.target.value as T["context"])} />
        </div>
        <div className="dial">
          <label className="dial-label" htmlFor="l1">Students' first language</label>
          <input id="l1" maxLength={60} placeholder="e.g. Malay, Tamil, Mandarin" value={value.l1} onChange={(e) => set("l1", e.target.value as T["l1"])} />
        </div>
      </div>

      <div className="dial">
        <label className="dial-label" htmlFor="int">What they're into</label>
        <input id="int" maxLength={160} placeholder="e.g. football, K-pop, mobile games, cooking" value={value.interests} onChange={(e) => set("interests", e.target.value as T["interests"])} />
      </div>

      <div className="dial">
        <span className="dial-label">Priorities <span className="muted">(up to 4)</span></span>
        <div className="chips">
          {EMPHASES.map((e) => (
            <button type="button" key={e} aria-pressed={value.emphasis.includes(e)} className={`chip${value.emphasis.includes(e) ? " on" : ""}`} onClick={() => toggleEmphasis(e)}>
              {EMPHASIS_LABELS[e]}
            </button>
          ))}
        </div>
      </div>

      <div className="dial">
        <label className="dial-label" htmlFor="notes">Anything else</label>
        <textarea id="notes" rows={2} maxLength={280} placeholder="e.g. Ramadan week, energy is low after lunch; two students are new arrivals" value={value.notes}
          onChange={(e) => set("notes", e.target.value as T["notes"])} />
      </div>
    </fieldset>
  );
}
